/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 02/05/22
 */

import React, { ReactNode } from 'react';
import { and, LayoutProps, RankedTester, rankWith, uiTypeIs } from '@jsonforms/core';
import { Heading, HeadingProps } from '@chakra-ui/react';
import { LabelElement } from '@jsonforms/core/src/models/uischema';
import { withNamedJsonFormsLayoutProps } from '@/forms-renderers/chakra-ui/utils/withNamedJsonFormsHOC';
import merge from 'lodash/merge';
import { namedMemo } from '@/utils/namedMemo';
import isEmpty from 'lodash/isEmpty';

const mapLevelToHeadingProps = (level: number, props: HeadingProps): HeadingProps => {
    const as: HeadingProps['as'][] = ['h2', 'h3', 'h4', 'h5', 'h6'];
    const size: HeadingProps['fontSize'][] = ['2xl', 'xl', 'lg', 'md', 'sm'];

    let index = level - 1;
    if (level <= 0 || level >= as.length) {
        index = 0;
    }

    return {
        as: as[index],
        fontSize: props.fontSize ?? size[index],
        color: props.color,
    };
};

export const ChakraHeading = ({ level, children, ...props }: { level: number; children: ReactNode } & HeadingProps) => {
    return (
        <Heading whiteSpace="pre-line" {...mapLevelToHeadingProps(level, props)}>
            {children}
        </Heading>
    );
};

interface HeadingOptions {
    headingLevel: 1 | 2 | 3 | 4 | 5;
    color?: string;
}

export const ChakraHeadingRenderer = namedMemo(
    'ChakraHeadingRenderer',
    ({ uischema, visible, config }: LayoutProps) => {
        const labelElement = uischema as LabelElement;

        const appliedUiSchemaOptions: HeadingOptions = merge({}, config, uischema.options);

        if (visible) {
            return (
                <ChakraHeading level={appliedUiSchemaOptions.headingLevel} {...appliedUiSchemaOptions}>
                    {labelElement.text}
                </ChakraHeading>
            );
        } else {
            return null;
        }
    },
);

export const chakraHeadingRendererTester: RankedTester = rankWith(
    1,
    and(
        uiTypeIs('Heading'),
        (uischema) =>
            !isEmpty(uischema) &&
            !!uischema.options &&
            !isEmpty(uischema.options) &&
            'headingLevel' in uischema.options &&
            typeof uischema.options.headingLevel === 'number' &&
            (uischema.options as HeadingOptions).headingLevel >= 1 &&
            (uischema.options as HeadingOptions).headingLevel <= 5,
    ),
);

export default withNamedJsonFormsLayoutProps(ChakraHeadingRenderer);
